import { FormInstance } from "antd";
import { SorterResult } from "antd/es/table/interface";
import { PartnerManagementRequestModel } from "api/repositories/partnerManagement/model/PartnerManagementRequestModel";
import { PartnerManagementResponseModel } from "api/repositories/partnerManagement/model/PartnerManagementResponseModel";
import {
  Error,
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { CreateBalanceTicketRequestModel } from "features/balanceHistory/data/model/actions/CreateBalanceTicketRequestModel";
import { UpdateBalanceTicketRequestModel } from "features/balanceHistory/data/model/actions/UpdateBalanceTicketRequestModel";
import { BalanceHistoryDetailResponseModel } from "features/balanceHistory/data/model/details/BalanceHistoryDetailResponseModel";
import { BalanceHistoryInjection } from "features/balanceHistory/di/BalanceHistoryInjection";
import { BalanceHistoryActionEntity } from "features/balanceHistory/domain/entities/BalanceHistoryActionEntity";
import { BalanceHistoryDetailEntity } from "features/balanceHistory/domain/entities/BalanceHistoryDetailEntity";
import { BalanceHistoryRequestEntity } from "features/balanceHistory/domain/entities/BalanceHistoryRequestEntity";
import { BalanceHistoryResponseEntity } from "features/balanceHistory/domain/entities/BalanceHistoryResponseEntity";
import { TransactionForRefundRequestEntity } from "features/balanceHistory/domain/entities/TransactionForRefundRequestEntity";
import { TransactionForRefundResponseEntity } from "features/balanceHistory/domain/entities/TransactionForRefundResponseEntity";
import { isEmpty } from "lodash";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { ProviderCodeEnum } from "utils/enums/ProviderCodeEnum";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";

class BalanceHistoryInput implements Disposable {
  public loading = new BehaviorSubject<boolean>(false);
  public actionLoadng = new BehaviorSubject<boolean>(false);
  public refundLoading = new BehaviorSubject<boolean>(false);
  public fetchHistory = new BehaviorSubject<BalanceHistoryRequestEntity>({});
  public userLoading = new BehaviorSubject<boolean>(false);
  public fetchHistoryForRefund =
    new BehaviorSubject<TransactionForRefundRequestEntity>(
      new TransactionForRefundRequestEntity(),
    );
  public fetchDetail = new BehaviorSubject<BalanceHistoryDetailResponseModel>(
    {},
  );
  public fetchUser = new BehaviorSubject<BaseUserRequestModel>({});
  public fetchPartner = new BehaviorSubject<PartnerManagementRequestModel>({});
  public fetchActions = new BehaviorSubject<{
    value?: BalanceHistoryActionEntity;
    type?: "create" | "update";
  }>({});

  [Symbol.dispose](): void {
    this.loading.complete();
    this.actionLoadng.complete();
    this.refundLoading.complete();
    this.fetchHistory.complete();
    this.fetchDetail.complete();
    this.fetchUser.complete();
    this.fetchHistoryForRefund.complete();
    this.fetchPartner.complete();
    this.userLoading.complete();
    this.fetchActions.complete();
  }
}

class BalanceHistoryOutput implements Disposable {
  public savedHistory = new BehaviorSubject<
    Array<BalanceHistoryResponseEntity>
  >([]);
  public savedDetail = new BehaviorSubject<BalanceHistoryDetailEntity>({});
  public savedUserList = new BehaviorSubject<Array<BaseUserResponseModel>>([]);
  public savedTransListForRefund = new BehaviorSubject<
    Array<TransactionForRefundResponseEntity>
  >([]);
  public savedPartnerList = new BehaviorSubject<
    Array<PartnerManagementResponseModel>
  >([]);
  public pagination = new BehaviorSubject<Paging>({});
  public refundPagination = new BehaviorSubject<Paging>({});
  public userPaging = new BehaviorSubject<Paging>({});
  public onError = new BehaviorSubject<Error>({});
  public createUpdateTickerSuccess = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.savedHistory.complete();
    this.savedDetail.complete();
    this.savedUserList.complete();
    this.savedPartnerList.complete();
    this.savedTransListForRefund.complete();
    this.pagination.complete();
    this.refundPagination.complete();
    this.userPaging.complete();
    this.onError.complete();
    this.createUpdateTickerSuccess.complete();
  }
}

@injectable()
export class BalanceHistoryViewModel extends BaseViewModel<
  BalanceHistoryInput,
  BalanceHistoryOutput
> {
  constructor() {
    super(new BalanceHistoryInput(), new BalanceHistoryOutput());
  }
  private balanceHistoryUseCase =
    BalanceHistoryInjection.getBalanceHistoryUseCase();

  public buildTableQuery(
    pagination: any,
    sorter: SorterResult,
    filterForm: React.RefObject<FormInstance<any>>,
    prevParams: BalanceHistoryRequestEntity,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    const { created, ...rest } = formValues;

    const order =
      sorter?.order === "descend"
        ? `${String(sorter?.column?.dataIndex)} DESC`
        : sorter?.order === "ascend"
          ? `${String(sorter?.column?.dataIndex)} ASC`
          : `createdAt DESC`;

    const query = {
      ...prevParams,
      ...rest,
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
      order,
    };

    const paginationState = {
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
    };

    return {
      query,
      pagination: paginationState,
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchHistory, async (params) => {
      try {
        this.output.savedHistory.next([]);
        this.input.loading.next(true);
        const res = await this.balanceHistoryUseCase.getList(params);
        if (res?.data) {
          this.output.savedHistory.next(res?.data);
          this.output.pagination.next(res?.pagination);
        }
      } catch (error) {
        console.error(error);
        this.output.savedHistory.next([]);
        this.output.pagination.next({});
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchHistoryForRefund, async (params) => {
      try {
        if (params?.limit === 0) {
          this.output.savedTransListForRefund.next([]);
          this.output.refundPagination.next({
            page: 0,
            limit: 20,
            total: 0,
          });
        } else {
          this.input.refundLoading.next(true);
          const res =
            await this.balanceHistoryUseCase.getTransactionForRefund(params);
          if (res?.data) {
            this.output.savedTransListForRefund.next(res?.data);
            this.output.refundPagination.next(res?.pagination);
          }
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.refundLoading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (value) => {
      try {
        this.output.savedDetail.next(value);
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchUser, async (params) => {
      try {
        this.input.userLoading.next(true);
        const isStore = String(params?.isStore) === "true";
        delete params?.isStore;
        const res = await this.balanceHistoryUseCase.getStaffList(params);
        if (res) {
          let finalData = res?.data[0]?.users?.filter((item) =>
            params?.providerCode === ProviderCodeEnum.mobitopup &&
            isStore === true
              ? item?.type === 3
              : item,
          );
          this.output.savedUserList.next(finalData);
          this.output.userPaging.next(res?.pagination);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.input.userLoading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchPartner, async (params) => {
      const cache = productCache.getCache(CacheKeyEnum.partnerList);
      if (!isEmpty(cache)) {
        this.output.savedPartnerList.next(cache);
      } else {
        try {
          const res = await this.balanceHistoryUseCase.getPartnerList(params);
          if (res) {
            this.output.savedPartnerList.next(res?.data);
            productCache.saveCache({
              key: CacheKeyEnum.partnerList,
              product: res?.data,
            });
          }
        } catch (error) {
          console.error(error);
        }
      }
    });

    this.skipFirstInitial(this.input.fetchActions, async (props) => {
      const { type, value } = props;
      switch (type) {
        case "create":
          let body = value as CreateBalanceTicketRequestModel;
          try {
            this.input.actionLoadng.next(true);
            const res = await this.balanceHistoryUseCase?.createTicket(body);
            if (res) {
              getMessage(
                strings.BalanceHistory.Message.CreateSuccessfully,
                4,
                "success",
              );
              this.output.createUpdateTickerSuccess.next(true);
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          } finally {
            this.input.actionLoadng.next(false);
          }
          break;
        case "update":
          let update = value as UpdateBalanceTicketRequestModel;
          try {
            this.input.actionLoadng.next(true);
            const res = await this.balanceHistoryUseCase?.updateTicket(update);
            if (res) {
              switch (update?.status) {
                case 2:
                  getMessage(
                    strings.BalanceHistory.Message.ApproveSuccessfully,
                    4,
                    "success",
                  );
                  this.output.createUpdateTickerSuccess.next(true);
                  break;
                case 9:
                  getMessage(
                    strings.BalanceHistory.Message.RejectSuccessfully,
                    4,
                    "success",
                  );
                  this.output.createUpdateTickerSuccess.next(true);
              }
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          } finally {
            this.input.actionLoadng.next(false);
          }
      }
    });
  }
}
